import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../store/slice';

// PACKAGES
import ScrollContainer from 'react-indiana-drag-scroll';
import Moment from 'moment';
import { Spinner } from 'react-bootstrap';

// COMPONENTS
import StatusCell from './StatusCell';
import ScrollButtons from './ScrollButtons';
import SortButton from './SortButton';
import PaginationCustom from '../../Pagination/Pagination';

// UTILS
import SvgMask from '../../_Helpers/SvgMask';
import { guid } from '../../../libs/utils';
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';
import DelayedFadeIn from '../../_Helpers/DelayedFadeIn';

// TYPES
import apibridge, { GetAssessmentOverviewTypes } from '../../../apibridge';
import { AssessmentSummaryOverviewOverviewInfo, AssessmentSummaryOverviewOverviewItem } from '../../../api/models';

interface AssessmentOverviewTableProps {
	shouldHighlightInFTUX: boolean;
	assessmentSummaryParams: GetAssessmentOverviewTypes['OverviewParams'];
	setAssessmentSummaryParams: React.Dispatch<React.SetStateAction<GetAssessmentOverviewTypes['OverviewParams']>>;
	onStudentClick: (classId: string, studentId: string) => Promise<void>;
	showMoveRecordCheckboxes: boolean;
	selectedMovingStudents: string[];
	setSelectedMovingStudents: React.Dispatch<React.SetStateAction<string[]>>;
}

const AssessmentOverviewTable: FC<AssessmentOverviewTableProps> = ({
	shouldHighlightInFTUX,
	assessmentSummaryParams,
	setAssessmentSummaryParams,
	onStudentClick,
	showMoveRecordCheckboxes,
	selectedMovingStudents,
	setSelectedMovingStudents
}) => {
	const dispatch = useAppDispatch();

	const [isLoading, setIsLoading] = useState(true);
	const [isReloading, setIsReloading] = useState(false);
	const [assessmentOverviewData, setAssessmentOverviewData] = useState<AssessmentSummaryOverviewOverviewInfo>();

	const {
		assessmentInfos,
		currentPage = 0,
		itemsPerPage = 0,
		stages = [],
		students = [],
		totalItems = 0,
		totalPages = 0
	} = assessmentOverviewData || {};

	// Scroll Logic
	const [scrollPosition, setScrollPosition] = useState(0);
	const [scrollWidth, setScrollWidth] = useState(0);

	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const getAssessmentOverviewData = async () => {
		setIsReloading(true);

		const response = await apibridge.getAssessmentSummaryOverview(assessmentSummaryParams);
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setAssessmentOverviewData(response.data.result);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessments overview error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
		setIsReloading(false);
	};

	useEffect(() => {
		if (!isLoading && scrollContainerRef.current) {
			const containerWidth = scrollContainerRef.current.clientWidth;
			const contentWidth = scrollContainerRef.current.scrollWidth;
			const maxScroll = contentWidth - containerWidth;
			setScrollWidth(maxScroll);
		}
	}, [isLoading]);

	useEffect(() => {
		getAssessmentOverviewData();
	}, [assessmentSummaryParams]); // eslint-disable-line react-hooks/exhaustive-deps

	// Reset page to 1 when params change, otherwise pagination will break
	useEffect(() => {
		if ((assessmentSummaryParams.pageNumber || 1) !== 1) {
			setAssessmentSummaryParams({ ...assessmentSummaryParams, pageNumber: 1 });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assessmentSummaryParams._class, assessmentSummaryParams.assessmentPeriodId, assessmentSummaryParams.benchmark]);

	// Checkbox functionality
	const handleSelectAll = () => {
		if (selectedMovingStudents.length === students.length) {
			// If all are selected, deselect all (empty state)
			setSelectedMovingStudents([]);
		} else if (selectedMovingStudents.length === 0) {
			// If none are selected, select all (checked state)
			const allStudentIds = students.map((student) => student.id || '').filter((id) => id !== '');
			setSelectedMovingStudents(allStudentIds);
		} else {
			// If some are selected (indeterminate state), select all (checked state)
			const allStudentIds = students.map((student) => student.id || '').filter((id) => id !== '');
			setSelectedMovingStudents(allStudentIds);
		}
	};

	const handleSelectStudent = (studentId: string) => {
		if (selectedMovingStudents.includes(studentId)) {
			// Remove student from selection
			setSelectedMovingStudents((prev) => prev.filter((id) => id !== studentId));
		} else {
			// Add student to selection
			setSelectedMovingStudents((prev) => [...prev, studentId]);
		}
	};

	// Calculate checkbox states
	const totalStudents = students.length;
	const selectedCount = selectedMovingStudents.length;

	const isAllSelected = totalStudents > 0 && selectedCount === totalStudents;
	const isIndeterminate = selectedCount > 0 && selectedCount < totalStudents;
	const isEmpty = selectedCount === 0;

	// Use ref to manage indeterminate state
	const headerCheckboxRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (headerCheckboxRef.current) {
			headerCheckboxRef.current.indeterminate = isIndeterminate;
		}
	}, [isIndeterminate, isAllSelected, isEmpty, selectedCount, totalStudents]);

	return (
		<div className="assessment-general-table">
			{isLoading ? (
				<div className="d-flex align-items-center justify-content-center position-relative w-100">
					<DelayedFadeIn>
						<Spinner animation="border" role="status">
							<span className="visually-hidden">Loading...</span>
						</Spinner>
					</DelayedFadeIn>
				</div>
			) : (
				<>
					<div className={`table-container text-shades-800 ${isReloading ? 'opacity-75' : ''}`}>
						{/* STUDENT DETAILS COLUMN */}
						<div className={`table-col-students ${showMoveRecordCheckboxes ? 'with-move-record-checkboxes' : ''}`}>
							{/* Student Name List Header */}
							<div className="student-row-heading border-bottom-0"></div>

							<div className="student-row-heading">
								{showMoveRecordCheckboxes && (
									<div className="d-flex align-items-center ps-3">
										<input
											type="checkbox"
											checked={isAllSelected}
											ref={headerCheckboxRef}
											onChange={handleSelectAll}
											onClick={(e) => e.stopPropagation()}
										/>
									</div>
								)}

								<div className="student-name-cell d-flex align-items-center justify-content-between gap-3">
									<span className="body-tiny">
										<strong>Student name</strong>
									</span>

									<SortButton
										type="Student"
										params={assessmentSummaryParams}
										setParams={setAssessmentSummaryParams}
										disabled={isReloading}
									/>
								</div>

								<div className="last-recorded-cell">
									<span className="body-tiny">
										<strong>Last recorded</strong>
									</span>

									<SortButton
										type="LastRecorded"
										params={assessmentSummaryParams}
										setParams={setAssessmentSummaryParams}
										disabled={isReloading}
									/>
								</div>
							</div>

							{/* Student name List */}
							{students.length > 0 ? (
								<div className="student-buttons-wrapper">
									{students.map((student, studentIndex) => {
										const enableHighlight = shouldHighlightInFTUX && studentIndex === 0;
										return (
											<button
												id={enableHighlight ? 'preselect-student-button' : ''}
												className={`btn btn-student student-row d-flex justify-content-between ${
													studentIndex % 2 === 0 ? 'bg-white' : ''
												} ${enableHighlight ? 'highlight-box-inset' : ''}`}
												key={studentIndex}
												onClick={() => onStudentClick(student.classId || '', student.id || '')}
											>
												{showMoveRecordCheckboxes && (
													<div className="d-flex align-items-center ps-3">
														<input
															type="checkbox"
															checked={selectedMovingStudents.includes(student.id || '')}
															onChange={() => handleSelectStudent(student.id || '')}
															onClick={(e) => e.stopPropagation()}
														/>
													</div>
												)}

												<div className="d-flex w-100">
													<div className={`student-name-cell d-flex flex-column justify-content-center flex-grow-1 `}>
														<span className="student-name body-small">
															<strong>
																{student.firstName} {student.lastName}
															</strong>
														</span>

														<span className="body-mini fw-semibold opacity-50">{student.className}</span>
													</div>

													<div className="last-recorded-cell">
														<span className="body-tiny fw-semibold">
															{Moment(student.lastRecorded).format('DD/MM/YY')}
														</span>

														<div className="edit-btn">
															<SvgMask path="/svg/edit.svg" width={24} height={24} />
														</div>
													</div>
												</div>
											</button>
										);
									})}
								</div>
							) : (
								<div className="student-row d-flex justify-content-between">
									<div className="student-name-cell d-flex flex-column justify-content-center">
										<p className="my-3">No results</p>
									</div>
								</div>
							)}
						</div>

						{/* SCROLL CONTAINER */}
						<div className="table-col-content d-flex scroll-btn-anchor">
							<ScrollButtons
								scrollPosition={scrollPosition}
								setScrollPosition={setScrollPosition}
								scrollWidth={scrollWidth}
								scrollContainerRef={scrollContainerRef}
							/>
							<ScrollContainer
								className="stages-scroll-container"
								hideScrollbars={true}
								vertical={false}
								innerRef={scrollContainerRef}
								onScroll={() => {
									if (scrollContainerRef.current) {
										setScrollPosition(scrollContainerRef.current.scrollLeft);
									}
								}}
							>
								{/* Header */}
								<div className="d-flex">
									{stages.map((stage, stageIndex) => {
										const { id, name = '' } = stage;
										const stageClassName = name.replace('.', '-');

										return (
											<div key={stageIndex} className="stage-col">
												{/* Stage Name */}
												<div className={`stage-name stage-${stageClassName}`}>Stage {name}</div>

												{/* Stage Progress Name */}
												<div className="d-flex">
													{assessmentInfos?.map((assessmentInfo) => {
														const { key, shortLabel } = assessmentInfo;

														return (
															<div key={`${key}-${stageIndex}`} className={`stage-progress stage-${stageClassName}`}>
																<span className="stage-progress-name">{shortLabel}</span>
																<SortButton
																	type={key === 'GraphemePhonemeKnowledge' ? 'Gpk' : key}
																	params={assessmentSummaryParams}
																	setParams={setAssessmentSummaryParams}
																	disabled={isReloading}
																	stageId={id}
																/>
															</div>
														);
													})}
												</div>
											</div>
										);
									})}
								</div>

								{/* Student Cells */}
								{students.length > 0 ? (
									<div className="table-row-wrapper">
										{students.map((student, studentIndex) => {
											return (
												<div key={studentIndex} className="table-row d-flex">
													{stages.map((stage, stageIndex) => {
														const mappedStage = student.items?.find((item) => item.stageId === stage.id);
														const items: AssessmentSummaryOverviewOverviewItem = {
															graphemePhoneme: mappedStage?.graphemePhoneme,
															reading: mappedStage?.reading,
															spelling: mappedStage?.spelling
														};

														return (
															<Fragment key={stageIndex}>
																<StatusCell
																	type="percentage"
																	percentage={items.graphemePhoneme}
																	className={`${studentIndex % 2 === 0 ? 'alternate-bg-white' : ''}`}
																/>

																<StatusCell
																	type="percentage"
																	percentage={items.reading}
																	className={`${studentIndex % 2 === 0 ? 'alternate-bg-white' : ''}`}
																/>

																<StatusCell
																	type="percentage"
																	percentage={items.spelling}
																	className={`${studentIndex % 2 === 0 ? 'alternate-bg-white' : ''}`}
																/>
															</Fragment>
														);
													})}
												</div>
											);
										})}
									</div>
								) : null}
							</ScrollContainer>
						</div>
					</div>

					{totalPages > 1 && (
						<div className="mt-5 d-flex justify-content-between align-items-center">
							<PaginationCustom
								pageNumber={assessmentSummaryParams.pageNumber || 1}
								totalPages={totalPages}
								pageSwitchCallback={(newPageNumber) => {
									setAssessmentSummaryParams({
										...assessmentSummaryParams,
										pageNumber: newPageNumber
									});
								}}
							/>

							<div className="body-small">
								<strong>
									Showing {itemsPerPage * (currentPage - 1) + 1} -{' '}
									{currentPage < totalPages ? itemsPerPage * currentPage : totalItems} of {totalItems}{' '}
									{totalItems === 1 ? 'student' : 'students'}
								</strong>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default AssessmentOverviewTable;
